/**
 * Spoken Word playback on the voice bus.
 * Loads `vo/{verseId}` MP3 clips + optional `.words.json` karaoke timings.
 */

import type { AudioBusGraph } from "./buses";
import type { ProclaimKaraokeDriver } from "./events";
import { linearKaraokeDriver } from "./events";
import type { AudioFrameInput } from "./frame";
import type { AudioLoader } from "./loader";
import type { AudioSettings } from "./settings";

export type VoWordTiming = {
  word: string;
  /** Start time in seconds from clip start */
  t: number;
  end: number;
};

export type VoWordsFile = {
  verseId?: string;
  words: VoWordTiming[];
};

function clipIdFor(verseId: string): string {
  return `vo.${verseId}`;
}

function pathFor(verseId: string): string {
  return `vo/${verseId}`;
}

export class VoicePlayer {
  private readonly context: AudioContext;
  private readonly graph: AudioBusGraph;
  private readonly loader: AudioLoader;

  private source: AudioBufferSourceNode | null = null;
  private gain: GainNode | null = null;
  private disposed = false;

  private activeVerseId = "";
  private words: VoWordTiming[] | null = null;
  /** AudioContext time when current source started (for offset resume). */
  private startedAt = 0;
  /** Offset into the buffer when current source started. */
  private startOffset = 0;
  private buffer: AudioBuffer | null = null;
  private paused = false;
  private playing = false;

  constructor(
    context: AudioContext,
    graph: AudioBusGraph,
    loader: AudioLoader,
  ) {
    this.context = context;
    this.graph = graph;
    this.loader = loader;
  }

  isPlaying(): boolean {
    return this.playing && !this.paused;
  }

  getActiveVerseId(): string {
    return this.activeVerseId;
  }

  /**
   * Preload VO buffers (and fire-and-forget words.json) for known verse ids.
   */
  async preload(verseIds: readonly string[]): Promise<void> {
    if (this.disposed) return;
    await Promise.all(
      verseIds.map(async (id) => {
        const clipId = clipIdFor(id);
        const existing = this.loader.peek(clipId);
        if (existing === undefined) {
          await this.loader.load(clipId, pathFor(id));
        }
        // Warm words cache (optional)
        void this.loadWords(id);
      }),
    );
  }

  private wordsCache = new Map<string, VoWordTiming[] | null>();

  private async loadWords(verseId: string): Promise<VoWordTiming[] | null> {
    if (this.wordsCache.has(verseId)) {
      return this.wordsCache.get(verseId) ?? null;
    }
    try {
      const res = await fetch(`/audio/vo/${verseId}.words.json`);
      if (!res.ok) {
        this.wordsCache.set(verseId, null);
        return null;
      }
      const data = (await res.json()) as VoWordsFile;
      const words = Array.isArray(data.words) ? data.words : null;
      this.wordsCache.set(verseId, words);
      return words;
    } catch {
      this.wordsCache.set(verseId, null);
      return null;
    }
  }

  /**
   * Start spoken Word for a proclaim. No-op if voice disabled or clip missing.
   */
  async start(
    verseId: string,
    settings: Pick<AudioSettings, "voiceEnabled" | "muted" | "voice">,
    offsetSec = 0,
  ): Promise<boolean> {
    if (this.disposed) return false;
    this.stopSourceOnly();

    if (!settings.voiceEnabled || settings.muted) {
      this.activeVerseId = verseId;
      this.words = (await this.loadWords(verseId)) ?? null;
      this.playing = false;
      this.paused = false;
      return false;
    }

    const clipId = clipIdFor(verseId);
    let buffer = this.loader.peek(clipId) ?? null;
    if (buffer === undefined || buffer === null) {
      buffer = await this.loader.load(clipId, pathFor(verseId));
    }
    if (!buffer || this.disposed) {
      this.activeVerseId = verseId;
      this.words = (await this.loadWords(verseId)) ?? null;
      this.playing = false;
      return false;
    }

    this.buffer = buffer;
    this.activeVerseId = verseId;
    this.words = (await this.loadWords(verseId)) ?? null;
    this.paused = false;

    const offset = Math.max(0, Math.min(offsetSec, Math.max(0, buffer.duration - 0.05)));
    return this.playFrom(offset);
  }

  private playFrom(offsetSec: number): boolean {
    if (!this.buffer || this.disposed) return false;

    this.stopSourceOnly();

    const source = this.context.createBufferSource();
    source.buffer = this.buffer;
    const gain = this.context.createGain();
    gain.gain.value = 1;

    source.connect(gain);
    gain.connect(this.graph.getBus("voice"));

    this.source = source;
    this.gain = gain;
    this.startOffset = offsetSec;
    this.startedAt = this.context.currentTime;
    this.playing = true;

    source.onended = () => {
      if (this.source === source) {
        this.playing = false;
        this.source = null;
        this.gain = null;
      }
      try {
        source.disconnect();
        gain.disconnect();
      } catch {
        /* */
      }
    };

    try {
      source.start(0, offsetSec);
      return true;
    } catch {
      this.playing = false;
      this.source = null;
      this.gain = null;
      return false;
    }
  }

  /** Elapsed seconds into the active clip (accounts for pause). */
  private elapsedSec(): number {
    if (!this.playing || this.paused) return this.startOffset;
    return this.startOffset + (this.context.currentTime - this.startedAt);
  }

  /**
   * Pause / resume with the sim. On pause: freeze playback at current offset.
   * On resume: continue if still proclaiming (caller passes settings).
   */
  onPause(
    paused: boolean,
    settings: Pick<AudioSettings, "voiceEnabled" | "muted">,
  ): void {
    if (this.disposed || !this.activeVerseId) return;
    if (paused) {
      if (this.playing && !this.paused) {
        this.startOffset = this.elapsedSec();
        this.stopSourceOnly();
        this.playing = true; // still "in" a proclaim VO, just frozen
        this.paused = true;
      }
    } else if (this.paused && settings.voiceEnabled && !settings.muted) {
      this.paused = false;
      if (this.buffer) {
        this.playFrom(this.startOffset);
      }
    } else {
      this.paused = false;
    }
  }

  /** Soft mute when frame.paused without full stop (belt-and-suspenders). */
  tick(frame: AudioFrameInput): void {
    if (this.disposed || !this.gain) return;
    const target = frame.paused ? 0 : 1;
    const g = this.gain.gain;
    const now = this.context.currentTime;
    g.cancelScheduledValues(now);
    g.setTargetAtTime(target, now, 0.04);
  }

  stop(): void {
    this.stopSourceOnly();
    this.activeVerseId = "";
    this.words = null;
    this.buffer = null;
    this.playing = false;
    this.paused = false;
    this.startOffset = 0;
  }

  private stopSourceOnly(): void {
    if (this.source) {
      try {
        this.source.onended = null;
        this.source.stop();
      } catch {
        /* */
      }
      try {
        this.source.disconnect();
      } catch {
        /* */
      }
      this.source = null;
    }
    if (this.gain) {
      try {
        this.gain.disconnect();
      } catch {
        /* */
      }
      this.gain = null;
    }
  }

  /**
   * Karaoke driver: uses word timings when available, else linear lock clock.
   */
  createKaraokeDriver(): ProclaimKaraokeDriver {
    const linear = linearKaraokeDriver();
    return {
      wordIndex: (lockElapsed, lockMax, wordCount) => {
        const words = this.words;
        if (!words || words.length === 0 || wordCount <= 0) {
          return linear.wordIndex(lockElapsed, lockMax, wordCount);
        }
        // Prefer VO clock when playing; else lock elapsed
        const t =
          this.playing && !this.paused ? this.elapsedSec() : lockElapsed;
        let idx = 0;
        for (let i = 0; i < words.length; i++) {
          if (words[i]!.t <= t + 1e-3) idx = i + 1;
          else break;
        }
        // Map timing word count → proclaim word count if they differ
        if (words.length === wordCount) {
          return Math.min(wordCount, idx);
        }
        const ratio = idx / Math.max(1, words.length);
        return Math.min(wordCount, Math.floor(ratio * wordCount + 1e-6));
      },
    };
  }

  resetMission(): void {
    this.stop();
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
    this.wordsCache.clear();
  }
}

/**
 * Verse ids that have VO under public/audio/vo/{id}.mp3 (+ optional .words.json).
 * Keep in sync with `npm run audio:vo -- --all` / deck catalog (all prototypes).
 */
export const PROCLAIM_VO_IDS: readonly string[] = [
  "2cor-10-5",
  "col-1-11",
  "col-3-15",
  "eph-6-16",
  "eph-6-17",
  "eccl-4-9-10",
  "gen-50-20",
  "heb-11-1",
  "isa-26-3",
  "isa-40-31",
  "jas-1-3-4",
  "jas-1-5",
  "jas-3-16-17",
  "1cor-14-33",
  "col-2-8",
  "john-14-27",
  "mark-11-22",
  "mark-11-23-24",
  "mark-4-39",
  "neh-8-10",
  "phil-4-6-7",
  "prov-3-6",
  "ps-29-11",
  "ps-46-10",
  "ps-56-3",
  "ps-91-1-2",
  "rom-10-17",
  "rom-15-13",
  "rom-5-3-4",
  "rom-8-28",
  "thess-5-17",
] as const;
